import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { X, Star, MapPin } from 'lucide-react';
import showToast from '../../ShowToast';
import './ModalStyles.css'; // Reuse existing modal styles

const BrowseProsModal = ({ isOpen, onClose, trade, professionId, postcode, taskId, onInvite }) => {
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [invitedPros, setInvitedPros] = useState([]);

    useEffect(() => {
        if (isOpen && (trade || professionId) && postcode) {
            fetchPros();
        }
    }, [isOpen, trade, professionId, postcode]);

    const fetchPros = async () => {
        setLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('postcode', postcode);
            params.append('radius', '50');
            
            if (professionId) {
                params.append('professionId', professionId);
            }
            // Always include trade if available, backend can decide priority or usage
            if (trade) {
                params.append('trade', trade);
            }
            
            const url = `${API_BASE_URL}/api/Jobs/find-local-pros?${params.toString()}`;
            const response = await axios.get(url);
            setPros(response.data);
        } catch (error) {
            console.error('Error fetching pros:', error);
            showToast('error', 'Failed to load professionals.');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (businessId, locationId) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/Jobs/invite-pro`, {
                taskId: taskId,
                businessId: businessId,
                locationId: locationId 
                // ProjectId can be passed if available in props, optional based on backend
            });
            
            setInvitedPros([...invitedPros, businessId]);
            showToast('success', 'Invitation sent successfully!');
            
            if (onInvite) {
                onInvite(); // Notify parent component to refresh task status
            }
        } catch (error) {
            console.error('Error inviting pro:', error);
            showToast('error', 'Failed to invite professional.');
        }
    };

    const handleViewProfile = (businessId) => {
        // Open profile in new tab
        window.open(`/business/${businessId}`, '_blank');
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content-large">
                <button className="modal-close-btn" onClick={onClose}>
                    <X size={24} />
                </button>
                <h2 style={{ marginTop: 0, textAlign: 'center' }}>Browse Local {trade}s</h2>
                <p className="modal-subtitle">Found {pros.length} professionals near {postcode}</p>
                
                <div>
                    {loading ? (
                        <div className="loading-spinner">Loading...</div>
                    ) : pros.length === 0 ? (
                        <p className="no-results">No local professionals found for this trade.</p>
                    ) : (
                        <div className="local-pros-list">
                            {pros.map(pro => (
                                <div key={pro.businessId} className="pro-card">
                                    <div className="pro-info">
                                        <h5 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>
                                            {pro.businessName} 
                                            <span style={{ fontSize: '0.9rem', color: '#6c757d', marginLeft: '8px' }}>
                                                <Star size={14} fill="#FFD700" stroke="#FFD700" style={{verticalAlign: 'text-bottom'}} /> {pro.rating.toFixed(1)} ({pro.reviewCount})
                                            </span>
                                        </h5>
                                        <p>{pro.description?.substring(0, 100)}...</p>
                                        <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                                            <MapPin size={12} /> {pro.distanceKm} km away
                                        </p>
                                    </div>
                                    <div className="pro-actions">
                                        <button 
                                            className="nav-button secondary" 
                                            onClick={() => handleViewProfile(pro.businessId)}
                                        >
                                            Profile
                                        </button>
                                        <button 
                                            className={`nav-button primary ${invitedPros.includes(pro.businessId) ? 'invited' : ''}`}
                                            onClick={() => handleInvite(pro.businessId, pro.locationId)} 
                                            disabled={invitedPros.includes(pro.businessId)}
                                        >
                                            {invitedPros.includes(pro.businessId) ? 'Invited' : 'Invite'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BrowseProsModal;
